// La placa de una categoría: una imagen con la pareja campeona, el podio y el
// camino hasta la final, para mandar al grupo de WhatsApp.
//
// Se dibuja en un <canvas>, en el celular, con datos que la pantalla ya tiene:
// no le pide nada al servidor ni suma dependencias. Mismo criterio que la placa
// del Ranking (de ahí salen cargarFuentes, aPng y las dos pasadas).

import type { Tema } from '../nucleo/temas';

export const ANCHO = 1080;
const MARGEN = 72;
const BLANCO = '#FFFFFF';
const APAGADO = 'rgba(255,255,255,0.64)';
const ORO = '#E6B84A';
const PLATA = '#C9D1D9';
const BRONCE = '#C7834A';

const TITULO = '"Bebas Neue", "Arial Narrow", sans-serif';
const TEXTO = '"Titillium Web", system-ui, sans-serif';

/**
 * Los colores de cada tema para la imagen. Son los de src/index.css: un canvas
 * no lee variables CSS de un tema que no está puesto en la página, así que van
 * repetidos acá. Sumar un tema = temas.ts + index.css + esta tabla.
 */
const COLORES: Record<Tema, { fondo: string; resplandor: string; acento: string }> = {
  fip: { fondo: '#031B3D', resplandor: '#00A3D9', acento: '#16C6EE' },
  verde: { fondo: '#0C1F17', resplandor: '#2FA36B', acento: '#F2A900' },
  rojo: { fondo: '#2A0C08', resplandor: '#D0492E', acento: '#F7F3EA' },
  negro: { fondo: '#070707', resplandor: '#5A5A5A', acento: '#D9F542' },
  rosa: { fondo: '#2A0A1D', resplandor: '#FF5FA2', acento: '#FF8CC0' },
};

export interface DatosDePlaca {
  torneo: string;
  /** "17 al 19 de octubre · Club La Esquina" */
  cuando: string;
  categoria: string;
  tema: Tema;
  campeona: string; // "Ferrero / Castro"
  subcampeona: string | null;
  semifinalistas: string[];
  /** Qué se lleva cada puesto; '' = nada escrito. */
  premios: { 1: string; 2: string; 3: string };
  /** Los partidos del cuadro de la campeona, en orden. `marcador` ya como texto, sus games primero. */
  camino: { ronda: string; rival: string; marcador: string }[];
  /** El link del torneo, para el pie. */
  link: string;
}

/** Sin esto, el primer dibujo sale con la fuente de reserva. */
function cargarFuentes(): Promise<unknown> {
  return Promise.all([
    document.fonts.load(`96px ${TITULO}`),
    document.fonts.load(`600 36px ${TEXTO}`),
    document.fonts.load(`36px ${TEXTO}`),
  ]).catch(() => undefined);
}

function aPng(lienzo: HTMLCanvasElement): Promise<Blob> {
  return new Promise((ok, mal) => lienzo.toBlob((b) => (b ? ok(b) : mal(new Error('No se pudo armar la imagen'))), 'image/png'));
}

export async function dibujarPlaca(datos: DatosDePlaca): Promise<Blob> {
  await cargarFuentes();

  // Dos pasadas: la primera en un lienzo alto de sobra, para medir; la segunda
  // del alto justo. Calcular el alto a mano se desfasa en cuanto cambia algo.
  const borrador = document.createElement('canvas');
  borrador.width = ANCHO;
  borrador.height = 5000;
  const fin = dibujar(borrador.getContext('2d')!, datos);

  const lienzo = document.createElement('canvas');
  lienzo.width = ANCHO;
  lienzo.height = Math.max(fin + 130, 1350);
  const c = lienzo.getContext('2d')!;
  fondo(c, lienzo.height, datos.tema);
  dibujar(c, datos);

  c.fillStyle = APAGADO;
  c.font = `28px ${TEXTO}`;
  c.fillText(datos.link.replace(/^https?:\/\//, '').replace(/\/$/, ''), MARGEN, lienzo.height - 60);

  return aPng(lienzo);
}

function fondo(c: CanvasRenderingContext2D, alto: number, tema: Tema) {
  const col = COLORES[tema];
  c.fillStyle = col.fondo;
  c.fillRect(0, 0, ANCHO, alto);
  // El resplandor que entra desde la izquierda, como en la app.
  const g = c.createRadialGradient(-80, alto * 0.2, 0, -80, alto * 0.2, ANCHO * 1.15);
  g.addColorStop(0, col.resplandor + 'AA');
  g.addColorStop(1, col.resplandor + '00');
  c.fillStyle = g;
  c.fillRect(0, 0, ANCHO, alto);
}

/** Achica la letra hasta que el texto entre en el ancho. Deja la fuente puesta. */
function ajustar(c: CanvasRenderingContext2D, texto: string, ancho: number, tamanio: number, minimo: number, fuente = TITULO): number {
  let t = tamanio;
  do {
    c.font = `${t}px ${fuente}`;
    if (c.measureText(texto).width <= ancho) break;
    t -= 4;
  } while (t > minimo);
  return t;
}

function caja(c: CanvasRenderingContext2D, y: number, alto: number, color: string) {
  c.strokeStyle = color;
  c.lineWidth = 4;
  c.beginPath();
  c.roundRect(MARGEN, y, ANCHO - 2 * MARGEN, alto, 28);
  c.stroke();
}

/** Todo menos el fondo y el pie. Devuelve hasta dónde llegó. */
function dibujar(c: CanvasRenderingContext2D, d: DatosDePlaca): number {
  const col = COLORES[d.tema];
  const util = ANCHO - 2 * MARGEN;
  const interior = util - 80;
  c.textAlign = 'left';
  c.textBaseline = 'alphabetic';

  // ── Cabecera ──
  let y = 120;
  c.fillStyle = col.acento;
  c.font = `44px ${TITULO}`;
  c.fillText('TORNEO DE PÁDEL', MARGEN, y);

  const t = ajustar(c, d.torneo.toUpperCase(), util, 116, 64);
  y += t + 4;
  c.fillStyle = BLANCO;
  c.fillText(d.torneo.toUpperCase(), MARGEN, y, util);

  y += 52;
  c.fillStyle = APAGADO;
  c.font = `32px ${TEXTO}`;
  c.fillText(d.cuando, MARGEN, y, util);

  y += 84;
  c.fillStyle = col.acento;
  ajustar(c, d.categoria.toUpperCase(), util, 68, 44);
  c.fillText(d.categoria.toUpperCase(), MARGEN, y, util);

  // ── La pareja campeona ──
  y += 50;
  const [j1, j2] = d.campeona.split(' / ');
  const altoCampeona = 150 + 112 * (j2 ? 2 : 1) + (d.premios[1] ? 56 : 0);
  caja(c, y, altoCampeona, ORO);
  let yy = y + 74;
  c.fillStyle = ORO;
  c.font = `44px ${TITULO}`;
  c.fillText('PAREJA CAMPEONA', MARGEN + 40, yy);
  c.fillStyle = BLANCO;
  for (const nombre of [j1, j2]) {
    if (!nombre) continue;
    yy += 112;
    ajustar(c, nombre.toUpperCase(), interior, 112, 64);
    c.fillText(nombre.toUpperCase(), MARGEN + 40, yy, interior);
  }
  if (d.premios[1]) {
    yy += 62;
    c.fillStyle = APAGADO;
    c.font = `32px ${TEXTO}`;
    c.fillText(d.premios[1], MARGEN + 40, yy, interior);
  }
  y += altoCampeona;

  // ── Subcampeona ──
  if (d.subcampeona) {
    y += 28;
    const alto = 156 + (d.premios[2] ? 46 : 0);
    caja(c, y, alto, PLATA);
    c.fillStyle = PLATA;
    c.font = `34px ${TITULO}`;
    c.fillText('PAREJA SUBCAMPEONA', MARGEN + 40, y + 58);
    c.fillStyle = BLANCO;
    ajustar(c, d.subcampeona.toUpperCase(), interior, 64, 40);
    c.fillText(d.subcampeona.toUpperCase(), MARGEN + 40, y + 124, interior);
    if (d.premios[2]) {
      c.fillStyle = APAGADO;
      c.font = `28px ${TEXTO}`;
      c.fillText(d.premios[2], MARGEN + 40, y + 170, interior);
    }
    y += alto;
  }

  // ── Semifinalistas ──
  if (d.semifinalistas.length > 0) {
    y += 28;
    const alto = 92 + 56 * d.semifinalistas.length + (d.premios[3] ? 46 : 0);
    caja(c, y, alto, BRONCE);
    c.fillStyle = BRONCE;
    c.font = `34px ${TITULO}`;
    c.fillText('SEMIFINALISTAS', MARGEN + 40, y + 58);
    c.fillStyle = BLANCO;
    let ys = y + 62;
    for (const nombre of d.semifinalistas) {
      ys += 56;
      c.font = `600 38px ${TEXTO}`;
      c.fillText(nombre, MARGEN + 40, ys, interior);
    }
    if (d.premios[3]) {
      c.fillStyle = APAGADO;
      c.font = `28px ${TEXTO}`;
      c.fillText(d.premios[3], MARGEN + 40, ys + 48, interior);
    }
    y += alto;
  }

  // ── El camino ──
  if (d.camino.length > 0) {
    y += 86;
    c.fillStyle = col.acento;
    c.font = `40px ${TITULO}`;
    c.fillText('EL CAMINO AL TÍTULO', MARGEN, y);
    c.fillStyle = 'rgba(255,255,255,0.25)';
    c.fillRect(MARGEN, y + 16, util, 2);
    y += 20;
    for (const paso of d.camino) {
      y += 62;
      c.fillStyle = APAGADO;
      c.font = `30px ${TEXTO}`;
      c.fillText(paso.ronda, MARGEN, y);
      c.fillStyle = BLANCO;
      c.font = `600 34px ${TEXTO}`;
      c.textAlign = 'right';
      c.fillText(paso.marcador, ANCHO - MARGEN, y);
      const anchoMarcador = c.measureText(paso.marcador).width;
      c.textAlign = 'left';
      c.font = `34px ${TEXTO}`;
      c.fillText(`a ${paso.rival}`, MARGEN + 230, y, util - 230 - anchoMarcador - 30);
    }
  }

  return y;
}
